import React, { useState } from 'react';
import { Flex } from '@chakra-ui/react';
import { useFormContext } from 'react-hook-form';
import { Option } from 'types/general.types';
import { InputField } from 'app/components/Core';
import { SignUpFormFields } from 'app/components/SignUp/SignUpForm/types';
import { ChakraAutocompleteField } from 'app/components/Core/ChakraAutocompleteField/ChakraAutocompleteField';
import { concatFirstAndLastName } from 'app/components/SignUp/utils';
import { HelpTooltip } from 'app/components/HelpTooltip';
import { AuthFormHeader } from 'app/components/AuthForm';

interface SignUpDetailsFormProps {
  countries: Option[];
}

export const SignUpDetailsForm: React.FC<SignUpDetailsFormProps> = ({
  countries,
}) => {
  const {
    getValues,
    setValue,
    trigger,
    register,
    formState: { errors },
    control,
  } = useFormContext<SignUpFormFields>();

  const [displayNameChangedByUser, setDisplayNameChangedByUser] = useState(
    false
  );

  const shouldPopulateDisplayName = (
    firstNameValue: string,
    lastNameValue: string
  ) => (firstNameValue || lastNameValue) && !displayNameChangedByUser;

  const onBlurFirstAndLastName = () => {
    const firstNameValue = getValues('firstName');
    const lastNameValue = getValues('lastName');

    if (shouldPopulateDisplayName(firstNameValue, lastNameValue)) {
      const displayName = concatFirstAndLastName(firstNameValue, lastNameValue);
      setValue('displayName', displayName);
      trigger('displayName');
    }
  };

  return (
    <Flex direction="column">
      <AuthFormHeader mb={12} heading="Create an account">
        Nice. What’s your
        <br />
        name and location?
      </AuthFormHeader>
      <InputField
        label="First Name"
        isInvalid={Boolean(errors.firstName)}
        errorMessage={errors.firstName?.message}
        data-testid="first-name"
        {...register('firstName', { onBlur: onBlurFirstAndLastName })}
      />
      <InputField
        label="Last Name"
        isInvalid={Boolean(errors.lastName)}
        errorMessage={errors.lastName?.message}
        data-testid="last-name"
        {...register('lastName', { onBlur: onBlurFirstAndLastName })}
      />
      <InputField
        label="Display Name"
        isInvalid={Boolean(errors.displayName)}
        errorMessage={errors.displayName?.message}
        data-testid="display-name"
        rightElement={
          <HelpTooltip label="This is how you'll appear to other Session users" />
        }
        {...register('displayName', {
          onChange: () => {
            setDisplayNameChangedByUser(true);
          },
        })}
      />
      <InputField
        label="City"
        isInvalid={Boolean(errors.city)}
        errorMessage={errors.city?.message}
        data-testid="city"
        {...register('city')}
      />
      <ChakraAutocompleteField
        control={control}
        label="Country"
        name="country"
        options={countries}
        dataTestId="country"
        isClearable
      />
    </Flex>
  );
};
